/* eslint-disable max-lines */
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  Label,
  Modal,
  Table,
} from 'reactstrap';
import { toast } from 'react-toastify';
import ReactTooltip from 'react-tooltip';
import { useContext, useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import CommonHeader from '../../../components/Headers/CommonHeader';
import FranchiseeService from '../../../services/Franchisee';
import { SUPPORT_DEPARTMENTS } from '../Support/taxonomies/departments';
import { getCategoriesByDepartment } from '../Support/taxonomies';

export interface TrelloList {
  id: string;
  label: string;
}

export interface TrelloBoard {
  id: string;
  label: string;
  lists: TrelloList[];
}

export interface TrelloOrganization {
  id: string;
  label: string;
  boards: TrelloBoard[];
}

export type SchoolDisplayMode = 'label' | 'columns';
export type BoardConfigType = 'school-as-label' | 'school-as-list';

export interface DepartmentMapping {
  departmentId: string;
  categoryLabel?: string;
  organizationId: string;
  boardId: string;
  listId: string;
  organizationLabel?: string;
  boardLabel?: string;
  listLabel?: string;
  boardConfigType: BoardConfigType;
}

export interface ConfigSchool {
  departmentId: string;
  categoryId: string;
  categoryLabel?: string;
  organizationId: string;
  boardId: string;
  listId: string;
  organizationLabel?: string;
  boardLabel?: string;
  listLabel?: string;
  boardConfigType: BoardConfigType;
}

const SCHOOL_MODE_LABEL_HELP =
  'In this mode, support tickets are created in the Trello list/column you select. The school name or abbreviation appears as a label on the card.';

const SCHOOL_MODE_LIST_HELP =
  'In this mode, support tickets are created in the Trello list or column that matches the school(name or abbreviation). That target is usually a list/column named with the school name or abbreviation.';

const ATTACHMENT_MIN_SIZE_MB = 10;
const ATTACHMENT_MAX_SIZE_MB = 250;
const ATTACHMENT_SIZE_STEP_MB = 5;

const SCHOOL_TYPE_HELP_TIP = [
  'School as label',
  SCHOOL_MODE_LABEL_HELP,
  '',
  'School as list',
  SCHOOL_MODE_LIST_HELP,
].join('\n');

const FranchiseeIntegrationConfig = () => {
  const { id } = useParams<{ id: string }>();
  const { token }: any = useContext(AuthContext);
  const [isDisabled, setIsDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [trelloEnabled, setTrelloEnabled] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [franchiseeName, setFranchiseeName] = useState('');
  const [credentialsSaved, setCredentialsSaved] = useState(false);
  const [isEditingKeys, setIsEditingKeys] = useState(false);
  const [verifyData, setVerifyData] = useState<TrelloOrganization[] | null>(null);
  const [attachmentMaxSizeMb, setAttachmentMaxSizeMb] = useState(ATTACHMENT_MIN_SIZE_MB);
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [departmentMappings, setDepartmentMappings] = useState<Record<string, DepartmentMapping>>({});

  const configRows = useMemo(() => {
    const rows: { id: string; label: string; departmentId: string; departmentLabel: string }[] = [];
    SUPPORT_DEPARTMENTS.forEach((d) => {
      const cats = getCategoriesByDepartment(d.id);
      if (cats.length) {
        cats.forEach((c) => rows.push({ id: c.id, label: c.label, departmentId: d.id, departmentLabel: d.label }));
      } else {
        rows.push({ id: d.id, label: d.label, departmentId: d.id, departmentLabel: d.label });
      }
    });
    return rows;
  }, []);

  const getById = async () => {
    try {
      const franchiseeResult = await FranchiseeService(token).show(id);
      if (franchiseeResult?.data) {
        setFranchiseeName(franchiseeResult?.data?.name || '');
      }

      try {
        const integrationResult = await FranchiseeService(token).getIntegration(id);
        const integration = integrationResult?.data;
        if (integration && integration.type === 'trello') {
          setTrelloEnabled(true);
          setApiKey(integration.apiKey || '');
          setApiToken(integration.apiToken || '');
          setApiSecret(integration.apiSecret || '');
          setAttachmentMaxSizeMb(Number(integration.attachmentMaxSizeMb) || ATTACHMENT_MIN_SIZE_MB);
          setCredentialsSaved(!!(integration.apiKey && integration.apiToken));
          if (Array.isArray(integration.configSchools) && integration.configSchools.length > 0) {
            const mapped: Record<string, DepartmentMapping> = {};
            integration.configSchools.forEach((c: ConfigSchool) => {
              const key = c.categoryId || c.departmentId;
              if (!key || !c.departmentId || !c.organizationId || !c.boardId) return;
              const bct: BoardConfigType = c.boardConfigType || 'school-as-label';
              if (bct === 'school-as-label' && !c.listId) return;
              mapped[key] = {
                departmentId: c.departmentId,
                categoryLabel: c.categoryLabel,
                organizationId: c.organizationId,
                boardId: c.boardId,
                listId: bct === 'school-as-list' ? '' : c.listId,
                organizationLabel: c.organizationLabel,
                boardLabel: c.boardLabel,
                listLabel: bct === 'school-as-list' ? '' : c.listLabel,
                boardConfigType: bct,
              };
            });
            setDepartmentMappings(mapped);
          }
        } else {
          setTrelloEnabled(false);
          setApiKey('');
          setApiToken('');
          setCredentialsSaved(false);
          setDepartmentMappings({});
        }
      } catch (integrationError: any) {
        if (integrationError?.response?.status !== 404) {
          toast.error(integrationError?.response?.data?.message || 'Error loading integration');
        }
        setTrelloEnabled(false);
        setApiKey('');
        setApiToken('');
        setCredentialsSaved(false);
        setIsEditingKeys(false);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Invalid franchisee ID');
    }
  };

  useEffect(() => {
    if (id) {
      getById();
    }
  }, [id]);

  const fetchOrganizationsAndOpenModal = async () => {
    setLoading(true);
    setIsDisabled(true);
    try {
      const orgResult = await FranchiseeService(token).getIntegrationOrganizations(id);
      const organizations = orgResult?.data?.organizations ?? orgResult?.organizations ?? orgResult?.data;
      if (Array.isArray(organizations) && organizations.length > 0) {
        setVerifyData(organizations);
        setShowDepartmentModal(true);
      } else {
        toast.error('No organizations found.');
      }
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Error loading organizations.');
      toast.error(message);
    } finally {
      setLoading(false);
      setIsDisabled(false);
    }
  };

  const handleVerifyIntegration = async () => {
    if (!apiKey || !apiToken || !apiSecret) {
      toast.error('Please fill in API Key, API Token and API Secret');
      return;
    }
    if (credentialsSaved && !isEditingKeys) return;

    setLoading(true);
    setIsDisabled(true);

    try {
      const body = {
        type: 'trello',
        apiKey: apiKey,
        apiToken: apiToken,
        apiSecret: apiSecret,
      };

      await FranchiseeService(token).verifyIntegration(id, body);
      setCredentialsSaved(true);
      setIsEditingKeys(false);
      toast.success('Integration verified successfully!');
      const orgResult = await FranchiseeService(token).getIntegrationOrganizations(id);
      const organizations = orgResult?.data?.organizations ?? orgResult?.organizations ?? orgResult?.data;
      if (Array.isArray(organizations) && organizations.length > 0) {
        setVerifyData(organizations);
        setShowDepartmentModal(true);
        toast.success('Associate departments to Trello boards.');
      }
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Error verifying integration!');
      toast.error(message);
    } finally {
      setLoading(false);
      setIsDisabled(false);
    }
  };

  const updateDepartmentMapping = (
    rowId: string,
    field: keyof DepartmentMapping,
    value: string,
    valueLabel?: string,
    context?: { departmentId: string; categoryLabel: string },
  ) => {
    setDepartmentMappings((prev) => {
      const current = prev[rowId] || {
        departmentId: '',
        organizationId: '',
        boardId: '',
        listId: '',
        boardConfigType: 'school-as-label' as BoardConfigType,
      };
      const base = context
        ? { ...current, departmentId: context.departmentId, categoryLabel: context.categoryLabel }
        : current;
      if (field === 'organizationId') {
        return {
          ...prev,
          [rowId]: {
            ...base,
            organizationId: value,
            organizationLabel: valueLabel ?? '',
            boardId: '',
            boardLabel: '',
            listId: '',
            listLabel: '',
          },
        };
      }
      if (field === 'boardId') {
        return {
          ...prev,
          [rowId]: {
            ...base,
            boardId: value,
            boardLabel: valueLabel ?? '',
            listId: '',
            listLabel: '',
          },
        };
      }
      if (field === 'listId') {
        return {
          ...prev,
          [rowId]: { ...base, listId: value, listLabel: valueLabel ?? '' },
        };
      }
      if (field === 'boardConfigType' && value === 'school-as-list') {
        return {
          ...prev,
          [rowId]: {
            ...base,
            boardConfigType: 'school-as-list' as BoardConfigType,
            listId: '',
            listLabel: '',
          },
        };
      }
      return { ...prev, [rowId]: { ...base, [field]: value } };
    });
  };

  const selectedBoards = useMemo(() => {
    if (!verifyData) return {};
    const acc: Record<string, TrelloBoard[]> = {};
    configRows.forEach((row) => {
      const m = departmentMappings[row.id];
      if (!m?.organizationId) return;
      const org = verifyData.find((o) => o.id === m.organizationId);
      acc[row.id] = org?.boards ?? [];
    });
    return acc;
  }, [verifyData, departmentMappings, configRows]);

  const selectedLists = useMemo(() => {
    if (!verifyData) return {};
    const acc: Record<string, TrelloList[]> = {};
    configRows.forEach((row) => {
      const m = departmentMappings[row.id];
      const boards = selectedBoards[row.id];
      if (!m?.boardId || !boards) return;
      const board = boards.find((b) => b.id === m.boardId);
      acc[row.id] = board?.lists ?? [];
    });
    return acc;
  }, [verifyData, departmentMappings, selectedBoards, configRows]);

  const fulfilledMappingsList = useMemo(() => {
    const configRowIds = new Set(configRows.map((r) => r.id));
    const fromConfigRows = configRows
      .filter((row) => {
        const m = departmentMappings[row.id];
        if (!m?.organizationId || !m?.boardId) return false;
        const t = m.boardConfigType || 'school-as-label';
        if (t === 'school-as-list') return true;
        return !!m.listId;
      })
      .map((row) => {
        const m = departmentMappings[row.id]!;
        const bct: BoardConfigType = m.boardConfigType || 'school-as-label';
        const orgLabel = m.organizationLabel ?? verifyData?.find((o) => o.id === m.organizationId)?.label ?? '—';
        const boardLabel =
          m.boardLabel ??
          verifyData?.find((o) => o.id === m.organizationId)?.boards?.find((b) => b.id === m.boardId)?.label ??
          '—';
        const listLabel =
          bct === 'school-as-list'
            ? '—'
            : m.listLabel ??
              verifyData
                ?.find((o) => o.id === m.organizationId)
                ?.boards?.find((b) => b.id === m.boardId)
                ?.lists?.find((l) => l.id === m.listId)?.label ??
              '—';
        return {
          rowId: row.id,
          department: row.departmentLabel,
          category: row.label,
          organization: orgLabel,
          board: boardLabel,
          list: listLabel,
          boardConfigType: bct,
        };
      });
    const fromDepartmentOnly = Object.entries(departmentMappings)
      .filter(([key, m]) => {
        if (configRowIds.has(key) || !m?.departmentId || !m?.organizationId || !m?.boardId) return false;
        const t = m.boardConfigType || 'school-as-label';
        if (t === 'school-as-list') return true;
        return !!m.listId;
      })
      .map(([key, m]) => {
        const bct: BoardConfigType = m.boardConfigType || 'school-as-label';
        const orgLabel = m.organizationLabel ?? verifyData?.find((o) => o.id === m.organizationId)?.label ?? '—';
        const boardLabel =
          m.boardLabel ??
          verifyData?.find((o) => o.id === m.organizationId)?.boards?.find((b) => b.id === m.boardId)?.label ??
          '—';
        const listLabel =
          bct === 'school-as-list'
            ? '—'
            : m.listLabel ??
              verifyData
                ?.find((o) => o.id === m.organizationId)
                ?.boards?.find((b) => b.id === m.boardId)
                ?.lists?.find((l) => l.id === m.listId)?.label ??
              '—';
        const departmentLabel = SUPPORT_DEPARTMENTS.find((d) => d.id === m.departmentId)?.label ?? m.departmentId;
        return {
          rowId: key,
          department: departmentLabel,
          category: m.categoryLabel ?? '—',
          organization: orgLabel,
          board: boardLabel,
          list: listLabel,
          boardConfigType: bct,
        };
      });
    return [...fromConfigRows, ...fromDepartmentOnly];
  }, [departmentMappings, verifyData, configRows]);

  const buildConfigSchools = (): ConfigSchool[] => {
    return Object.entries(departmentMappings)
      .filter(([, m]) => {
        if (!m.departmentId || !m.organizationId || !m.boardId) return false;
        const t: BoardConfigType = m.boardConfigType || 'school-as-label';
        if (t === 'school-as-list') return true;
        return !!m.listId;
      })
      .map(([categoryId, m]) => {
        const t: BoardConfigType = m.boardConfigType || 'school-as-label';
        return {
          departmentId: m.departmentId,
          categoryId,
          categoryLabel: m.categoryLabel,
          organizationId: m.organizationId,
          boardId: m.boardId,
          listId: t === 'school-as-list' ? '' : m.listId,
          organizationLabel: m.organizationLabel,
          boardLabel: m.boardLabel,
          listLabel: t === 'school-as-list' ? '' : m.listLabel,
          boardConfigType: t,
        };
      });
  };

  const handleSaveDepartmentMappings = async () => {
    setLoading(true);
    setIsDisabled(true);
    try {
      const configSchools = buildConfigSchools();
      const payload: {
        type: string;
        apiKey?: string;
        apiToken?: string;
        apiSecret?: string;
        configSchools?: ConfigSchool[];
        attachmentMaxSizeMb?: number;
      } = {
        type: 'trello',
        configSchools: configSchools.length ? configSchools : undefined,
        attachmentMaxSizeMb,
      };
      if (isEditingKeys || !credentialsSaved) {
        payload.apiKey = apiKey;
        payload.apiToken = apiToken;
        if (apiSecret) payload.apiSecret = apiSecret;
      }
      await FranchiseeService(token).createOrUpdateIntegration(id, payload);
      toast.success('Department associations saved successfully.');
      setShowDepartmentModal(false);
      getById();
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Error saving.');
      toast.error(message);
    } finally {
      setLoading(false);
      setIsDisabled(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setIsDisabled(true);

    const configSchools = buildConfigSchools();
    let body: {
      type: string;
      apiKey?: string;
      apiToken?: string;
      apiSecret?: string;
      configSchools?: ConfigSchool[];
      attachmentMaxSizeMb?: number;
    } | null = null;
    if (trelloEnabled) {
      body = {
        type: 'trello',
        configSchools: configSchools.length ? configSchools : undefined,
        attachmentMaxSizeMb,
      };
      if (isEditingKeys || !credentialsSaved) {
        body.apiKey = apiKey;
        body.apiToken = apiToken;
        if (apiSecret) body.apiSecret = apiSecret;
      }
    }

    try {
      await FranchiseeService(token).createOrUpdateIntegration(id, body);
      setCredentialsSaved(trelloEnabled && !!apiKey && !!apiToken);
      toast.success('Integration configuration updated successfully!');
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Update error!');
      toast.error(message);
    } finally {
      setLoading(false);
      setIsDisabled(false);
    }
  };

  return (
    <>
      <CommonHeader />
      <Container className="mt--8" fluid>
        <Row>
          <Col className="order-xl-2" xl="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">Configure Support Integration - {franchiseeName}</h3>
                  </Col>
                  <Col className="text-right" xs="4">
                    <Link to="/franchisee">
                      <Button color="primary" className="float-right">
                        Back
                      </Button>
                    </Link>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          <Label check>
                            <Input
                              type="checkbox"
                              checked={trelloEnabled}
                              onChange={(e) => {
                                setTrelloEnabled(e.target.checked);
                                if (!e.target.checked) {
                                  setCredentialsSaved(false);
                                }
                              }}
                              disabled={isDisabled}
                            />{' '}
                            Trello
                          </Label>
                        </FormGroup>
                      </Col>
                    </Row>
                    {trelloEnabled && (
                      <>
                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label className="form-control-label" htmlFor="input-attachment-max-size">
                                Support attachment size limit
                              </label>
                              <div className="d-flex align-items-center">
                                <Input
                                  className="custom-range mr-3"
                                  id="input-attachment-max-size"
                                  type="range"
                                  min={ATTACHMENT_MIN_SIZE_MB}
                                  max={ATTACHMENT_MAX_SIZE_MB}
                                  step={ATTACHMENT_SIZE_STEP_MB}
                                  disabled={isDisabled}
                                  value={attachmentMaxSizeMb}
                                  onChange={(e) => setAttachmentMaxSizeMb(Number(e.target.value))}
                                />
                                <span className="font-weight-bold text-nowrap">{attachmentMaxSizeMb} MB</span>
                              </div>
                              <small className="text-muted d-block mt-2">
                                Maximum size per file (images, PDFs and videos) on support tickets.
                              </small>
                              {attachmentMaxSizeMb > ATTACHMENT_MIN_SIZE_MB && (
                                <small className="text-warning d-block mt-1">
                                  <i className="fas fa-exclamation-triangle mr-1" />
                                  Files above {ATTACHMENT_MIN_SIZE_MB}MB only reach Trello on a paid plan (up to{' '}
                                  {ATTACHMENT_MAX_SIZE_MB}MB). On the free plan they are stored on S3 only and will not
                                  be attached to the card.
                                </small>
                              )}
                            </FormGroup>
                          </Col>
                        </Row>
                        {credentialsSaved && !isEditingKeys ? (
                          <Row>
                            <Col lg="12">
                              <p className="text-success mb-2">
                                <i className="fas fa-check-circle mr-2" />
                                Credentials configured
                              </p>
                              <Button
                                color="secondary"
                                type="button"
                                disabled={isDisabled || loading}
                                onClick={() => {
                                  setIsEditingKeys(true);
                                  setApiKey('');
                                  setApiToken('');
                                }}
                                className="mb-3 mr-2"
                              >
                                Change keys
                              </Button>
                              <Button
                                color="info"
                                type="button"
                                disabled={isDisabled || loading}
                                onClick={fetchOrganizationsAndOpenModal}
                                className="mb-3"
                              >
                                Edit department mappings
                              </Button>
                              {fulfilledMappingsList.length > 0 && (
                                <Table className="align-items-center table-flush mb-3" responsive>
                                  <thead>
                                    <tr>
                                      <th>Department</th>
                                      <th>Category</th>
                                      <th>Organization</th>
                                      <th>Type</th>
                                      <th>Board</th>
                                      <th>List</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {fulfilledMappingsList.map((row) => (
                                      <tr key={row.rowId}>
                                        <td>{row.department}</td>
                                        <td>{row.category}</td>
                                        <td>{row.organization}</td>
                                        <td>
                                          {row.boardConfigType === 'school-as-label'
                                            ? 'School as label'
                                            : 'School as list'}
                                        </td>
                                        <td>{row.board}</td>
                                        <td>{row.list}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              )}
                            </Col>
                          </Row>
                        ) : (
                          <>
                            <Row>
                              <Col lg="6">
                                <FormGroup>
                                  <label className="form-control-label" htmlFor="input-api-key">
                                    API KEY
                                  </label>
                                  <Input
                                    className="form-control-alternative"
                                    id="input-api-key"
                                    placeholder="Trello API Key"
                                    type="text"
                                    disabled={isDisabled}
                                    value={apiKey}
                                    onChange={(e) => {
                                      setApiKey(e.target.value);
                                      setCredentialsSaved(false);
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                              <Col lg="6">
                                <FormGroup>
                                  <label className="form-control-label" htmlFor="input-api-token">
                                    API TOKEN
                                  </label>
                                  <Input
                                    className="form-control-alternative"
                                    id="input-api-token"
                                    placeholder="Trello API Token"
                                    type="text"
                                    disabled={isDisabled}
                                    value={apiToken}
                                    onChange={(e) => {
                                      setApiToken(e.target.value);
                                      setCredentialsSaved(false);
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row>
                              <Col lg="12">
                                <FormGroup>
                                  <label className="form-control-label" htmlFor="input-api-secret">
                                    API SECRET
                                  </label>
                                  <Input
                                    className="form-control-alternative"
                                    id="input-api-secret"
                                    placeholder="Trello API Secret (OAuth secret from the App, required for webhooks)"
                                    type="text"
                                    disabled={isDisabled}
                                    value={apiSecret}
                                    onChange={(e) => {
                                      setApiSecret(e.target.value);
                                      setCredentialsSaved(false);
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            {credentialsSaved && isEditingKeys && (
                              <Row>
                                <Col lg="12">
                                  <Button
                                    color="secondary"
                                    type="button"
                                    disabled={isDisabled || loading}
                                    onClick={() => {
                                      setIsEditingKeys(false);
                                      getById();
                                    }}
                                    className="mb-3 mr-2"
                                  >
                                    Cancel
                                  </Button>
                                </Col>
                              </Row>
                            )}
                            <Row>
                              <Col lg="12">
                                <Button
                                  color="info"
                                  type="button"
                                  disabled={isDisabled || loading || !apiKey || !apiToken || !apiSecret}
                                  onClick={handleVerifyIntegration}
                                  className="mb-3"
                                >
                                  Verify Integration
                                </Button>
                                {fulfilledMappingsList.length > 0 && (
                                  <Table className="align-items-center table-flush mb-3" responsive>
                                    <thead>
                                      <tr>
                                        <th>Department</th>
                                        <th>Category</th>
                                        <th>Organization</th>
                                        <th>Board</th>
                                        <th>List</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {fulfilledMappingsList.map((row) => (
                                        <tr key={row.rowId}>
                                          <td>{row.department}</td>
                                          <td>{row.category}</td>
                                          <td>{row.organization}</td>
                                          <td>{row.board}</td>
                                          <td>{row.list}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                )}
                              </Col>
                            </Row>
                          </>
                        )}
                      </>
                    )}
                  </div>
                  <div className="pl-lg-4">
                    <Button className="my-4" color="primary" type="submit" disabled={isDisabled || loading}>
                      Save
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        className="modal-dialog-centered modal-xl"
        isOpen={showDepartmentModal}
        toggle={() => setShowDepartmentModal(false)}
      >
        <div className="modal-header pb-1">
          <h5 className="modal-title">Associate departments to Trello</h5>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={() => setShowDepartmentModal(false)}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="modal-body pt-2">
          <p className="text-muted small mb-3">
            Select Organization, Board and List for each department and category. These will be used when creating
            support tickets.
          </p>
          <Table className="align-items-center table-flush" responsive>
            <thead>
              <tr>
                <th>Department</th>
                <th>Category</th>
                <th>Organization</th>
                <th>
                  <span className="align-middle">Type</span>
                  <button
                    type="button"
                    className="btn btn-link p-0 ml-1 align-middle text-muted"
                    style={{ lineHeight: 1, verticalAlign: 'middle' }}
                    data-tip={SCHOOL_TYPE_HELP_TIP}
                    data-multiline={true}
                    data-place="top"
                    data-type="info"
                    data-class="franchisee-dept-type-tooltip"
                    aria-label="What School as label and School as list mean"
                  >
                    <i className="fas fa-info-circle" aria-hidden={true} />
                  </button>
                </th>
                <th>Board</th>
                <th>List</th>
              </tr>
            </thead>
            <tbody>
              {configRows.map((row) => {
                const mapping = departmentMappings[row.id] || {
                  departmentId: '',
                  organizationId: '',
                  boardId: '',
                  listId: '',
                  boardConfigType: 'school-as-label' as BoardConfigType,
                };
                const boardConfigType: BoardConfigType = mapping.boardConfigType || 'school-as-label';
                const boards = selectedBoards[row.id] ?? [];
                const lists = selectedLists[row.id] ?? [];
                const context = { departmentId: row.departmentId, categoryLabel: row.label };
                const groupName = `board-config-type-${row.id}`;
                return (
                  <tr key={row.id}>
                    <td className="align-middle">{row.departmentLabel}</td>
                    <td className="align-middle">{row.label}</td>
                    <td>
                      <Input
                        type="select"
                        value={mapping.organizationId}
                        onChange={(e) => {
                          const sel = e.target as unknown as HTMLSelectElement;
                          updateDepartmentMapping(
                            row.id,
                            'organizationId',
                            sel.value,
                            sel.selectedOptions[0]?.text,
                            context,
                          );
                        }}
                      >
                        <option value="">Select...</option>
                        {(verifyData ?? []).map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.label}
                          </option>
                        ))}
                      </Input>
                    </td>
                    <td className="align-middle" style={{ minWidth: '14rem' }}>
                      <div
                        className="segmented-pill"
                        role="radiogroup"
                        aria-label="School representation in the board (label or columns)"
                      >
                        <label
                          className={
                            boardConfigType === 'school-as-label'
                              ? 'segmented-pill__segment is-active'
                              : 'segmented-pill__segment'
                          }
                        >
                          <Input
                            type="radio"
                            className="segmented-pill__input"
                            name={groupName}
                            value="school-as-label"
                            checked={boardConfigType === 'school-as-label'}
                            onChange={() =>
                              updateDepartmentMapping(row.id, 'boardConfigType', 'school-as-label', undefined, context)
                            }
                          />
                          <span className="segmented-pill__text">School as label</span>
                        </label>
                        <label
                          className={
                            boardConfigType === 'school-as-list'
                              ? 'segmented-pill__segment is-active'
                              : 'segmented-pill__segment'
                          }
                        >
                          <Input
                            type="radio"
                            className="segmented-pill__input"
                            name={groupName}
                            value="school-as-list"
                            checked={boardConfigType === 'school-as-list'}
                            onChange={() =>
                              updateDepartmentMapping(row.id, 'boardConfigType', 'school-as-list', undefined, context)
                            }
                          />
                          <span className="segmented-pill__text">School as list</span>
                        </label>
                      </div>
                    </td>
                    <td>
                      <Input
                        type="select"
                        value={mapping.boardId}
                        disabled={!mapping.organizationId}
                        onChange={(e) => {
                          const sel = e.target as unknown as HTMLSelectElement;
                          updateDepartmentMapping(row.id, 'boardId', sel.value, sel.selectedOptions[0]?.text, context);
                        }}
                      >
                        <option value="">Select...</option>
                        {boards.map((board) => (
                          <option key={board.id} value={board.id}>
                            {board.label}
                          </option>
                        ))}
                      </Input>
                    </td>
                    <td>
                      <Input
                        type="select"
                        value={boardConfigType === 'school-as-list' ? '' : mapping.listId}
                        disabled={!mapping.boardId || boardConfigType === 'school-as-list'}
                        onChange={(e) => {
                          const sel = e.target as unknown as HTMLSelectElement;
                          updateDepartmentMapping(row.id, 'listId', sel.value, sel.selectedOptions[0]?.text, context);
                        }}
                      >
                        <option value="">{boardConfigType === 'school-as-list' ? 'N/A' : 'Select...'}</option>
                        {boardConfigType !== 'school-as-list' &&
                          lists.map((list) => (
                            <option key={list.id} value={list.id}>
                              {list.label}
                            </option>
                          ))}
                      </Input>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <ReactTooltip effect="solid" multiline />
        </div>
        <div className="modal-footer">
          <Button color="secondary" onClick={() => setShowDepartmentModal(false)}>
            Close
          </Button>
          <Button color="primary" disabled={loading} onClick={handleSaveDepartmentMappings}>
            Save
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default FranchiseeIntegrationConfig;
